/**
 * PROTOTYPE UI — throwaway.
 * Three variants of web capture, switchable via ?variant=
 * Question: does capture feel fast enough (movable Composer, overlay, hover+T, ambient Event)?
 */
import { useEffect, useRef, type PointerEvent as ReactPointerEvent } from 'react'
import { BLOCKS, EVENT, PEOPLE, findRef, type ProtoRef } from './seed'
import type { PrototypeState } from './usePrototypeState'

function CaptureHotkeys({ state }: { state: PrototypeState }) {
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			const t = e.target as HTMLElement | null
			if (
				t &&
				(t.tagName === 'INPUT' ||
					t.tagName === 'TEXTAREA' ||
					t.isContentEditable)
			) {
				return
			}
			if (e.key === 't' || e.key === 'T') {
				e.preventDefault()
				state.handleT()
			}
			if (e.key === 'Escape' && state.open) {
				state.close()
			}
		}
		window.addEventListener('keydown', onKey)
		return () => window.removeEventListener('keydown', onKey)
	}, [state])
	return null
}

function Chrome({
	state,
	onAvatarControl,
}: {
	state: PrototypeState
	onAvatarControl: () => void
}) {
	return (
		<header className="flex items-center justify-between border-b border-white/10 bg-[#303535] px-4 py-2">
			<div className="flex items-center gap-3">
				<p className="text-xs font-bold uppercase tracking-wider text-[#88d8de]">
					PROTOTYPE
				</p>
				<div className="flex rounded-md bg-black/30 p-0.5 text-xs">
					{(
						[
							['attendance', 'Attendance'],
							['plan', 'Plan'],
						] as const
					).map(([key, label]) => (
						<button
							key={key}
							type="button"
							onClick={() => state.setRoom(key)}
							className={`rounded px-2 py-1 font-semibold ${
								state.room === key
									? 'bg-[#88d8de] text-[#303535]'
									: 'text-gray-300 hover:bg-white/10'
							}`}
						>
							{label}
						</button>
					))}
				</div>
				<p className="hidden text-xs text-gray-400 sm:block">
					Hover a ringed thing · press T · {EVENT.name} is ambient
				</p>
			</div>
			<div className="flex items-center gap-2">
				<button
					type="button"
					onClick={onAvatarControl}
					title="New Task"
					className="flex size-8 items-center justify-center rounded-full bg-[#ff585f] text-sm font-bold text-white"
				>
					+
				</button>
				<div
					className="flex size-8 items-center justify-center rounded-full bg-white text-xs font-bold text-[#303535]"
					title="You"
				>
					KL
				</div>
			</div>
		</header>
	)
}

function RefButton({
	item,
	state,
	className,
	children,
}: {
	item: ProtoRef
	state: PrototypeState
	className?: string
	children: React.ReactNode
}) {
	const attached = state.draft.references.some((r) => r.id === item.id)
	const hovered = state.hoveredId === item.id
	const showRing = state.open || hovered
	return (
		<button
			type="button"
			data-ref={item.id}
			onMouseEnter={() => state.setHoveredId(item.id)}
			onMouseLeave={() =>
				state.setHoveredId((id) => (id === item.id ? null : id))
			}
			onClick={() => {
				if (state.open) state.addRef(item)
				else state.openComposer(item)
			}}
			className={[
				className ?? '',
				showRing ? 'ring-2 ring-[#88d8de] ring-offset-2 ring-offset-[#303535]' : '',
				attached ? 'bg-[#88d8de]/20' : '',
			].join(' ')}
		>
			{children}
		</button>
	)
}

function AttendanceCanvas({ state }: { state: PrototypeState }) {
	return (
		<div className="grid grid-cols-2 gap-2 p-4 sm:grid-cols-3">
			{PEOPLE.map((person) => (
				<RefButton
					key={person.id}
					item={person}
					state={state}
					className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-left text-sm text-white hover:bg-white/10"
				>
					<span className="flex size-8 items-center justify-center rounded-full bg-[#88d8de] text-xs font-bold text-[#303535]">
						{person.name
							.split(' ')
							.map((w) => w[0])
							.join('')}
					</span>
					<span>{person.name}</span>
				</RefButton>
			))}
		</div>
	)
}

function PlanCanvas({ state }: { state: PrototypeState }) {
	return (
		<div className="space-y-2 p-4">
			{BLOCKS.map((block) => (
				<RefButton
					key={block.id}
					item={block}
					state={state}
					className="block w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-left hover:bg-white/10"
				>
					<p className="text-sm font-semibold text-white">{block.name}</p>
					<p className="text-[11px] text-gray-400">
						Plan block · {EVENT.name}
					</p>
				</RefButton>
			))}
		</div>
	)
}

function Canvas({ state }: { state: PrototypeState }) {
	return state.room === 'attendance' ? (
		<AttendanceCanvas state={state} />
	) : (
		<PlanCanvas state={state} />
	)
}

function DraftFields({ state }: { state: PrototypeState }) {
	return (
		<>
			<input
				value={state.draft.action}
				onChange={(e) =>
					state.setDraft((d) => ({ ...d, action: e.target.value }))
				}
				placeholder="Action"
				className="w-full rounded-md border border-white/15 bg-black/30 px-2 py-1.5 text-sm text-white placeholder:text-gray-500"
			/>
			<textarea
				value={state.draft.details}
				onChange={(e) =>
					state.setDraft((d) => ({ ...d, details: e.target.value }))
				}
				placeholder="Details"
				rows={2}
				className="w-full rounded-md border border-white/15 bg-black/30 px-2 py-1.5 text-sm text-white placeholder:text-gray-500"
			/>
			<div className="flex flex-wrap gap-1">
				{state.draft.references.map((ref) => (
					<button
						key={ref.id}
						type="button"
						onClick={() => state.removeRef(ref.id)}
						className="rounded-full bg-[#88d8de]/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#88d8de]"
						title="Remove Reference"
					>
						{ref.kind}: {ref.name} ×
					</button>
				))}
			</div>
		</>
	)
}

function StateDump({ state }: { state: PrototypeState }) {
	return (
		<pre className="max-h-28 overflow-auto rounded bg-black/50 p-2 text-[10px] leading-snug text-gray-400">
			{JSON.stringify(
				{
					room: state.room,
					open: state.open,
					hovered: state.hoveredId
						? findRef(state.hoveredId)?.name
						: null,
					draft: state.draft,
					saved: state.saved.map((s) => s.action),
					log: state.log,
				},
				null,
				2,
			)}
		</pre>
	)
}

function ComposerActions({ state }: { state: PrototypeState }) {
	return (
		<div className="flex gap-2">
			<button
				type="button"
				onClick={state.save}
				className="rounded-md bg-[#ff585f] px-2 py-1 text-xs font-bold text-white"
			>
				Save
			</button>
			<button
				type="button"
				onClick={state.newTask}
				className="rounded-md bg-white/10 px-2 py-1 text-xs font-semibold text-white"
			>
				New
			</button>
			<button
				type="button"
				onClick={state.close}
				className="rounded-md px-2 py-1 text-xs text-gray-400"
			>
				Close
			</button>
		</div>
	)
}

/** A — Movable floating Composer (locked model). */
export function VariantA({ state }: { state: PrototypeState }) {
	const drag = useRef<{ dx: number; dy: number } | null>(null)

	const onPointerDown = (e: ReactPointerEvent) => {
		if ((e.target as HTMLElement).closest('input, textarea, button')) return
		drag.current = {
			dx: e.clientX - state.pos.x,
			dy: e.clientY - state.pos.y,
		}
		;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
	}
	const onPointerMove = (e: ReactPointerEvent) => {
		if (!drag.current) return
		state.setPos({
			x: e.clientX - drag.current.dx,
			y: e.clientY - drag.current.dy,
		})
	}
	const onPointerUp = () => {
		drag.current = null
	}

	return (
		<div className="flex min-h-screen flex-col bg-[#303535] text-white">
			<CaptureHotkeys state={state} />
			<Chrome state={state} onAvatarControl={() => state.openComposer()} />
			<Canvas state={state} />
			{state.open ? (
				<div
					className="fixed z-50 w-80 cursor-grab rounded-xl border border-[#88d8de] bg-[#303535] p-3 shadow-2xl"
					style={{ left: state.pos.x, top: state.pos.y }}
					onPointerDown={onPointerDown}
					onPointerMove={onPointerMove}
					onPointerUp={onPointerUp}
				>
					<p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-gray-400">
						Composer · drag to move
					</p>
					<div className="space-y-2">
						<DraftFields state={state} />
						<ComposerActions state={state} />
						<StateDump state={state} />
					</div>
				</div>
			) : (
				<div className="mx-4 mb-20 rounded-lg border border-white/10 p-2">
					<StateDump state={state} />
				</div>
			)}
		</div>
	)
}

/** B — Composer docks next to the last hovered / T'd entity. */
export function VariantB({ state }: { state: PrototypeState }) {
	const hover = state.hoveredId ? findRef(state.hoveredId) : undefined
	return (
		<div className="flex min-h-screen flex-col bg-[#1b1f1f] text-white">
			<CaptureHotkeys state={state} />
			<Chrome
				state={state}
				onAvatarControl={() => state.openComposer()}
			/>
			<div className="grid flex-1 grid-cols-1 md:grid-cols-[1fr_20rem]">
				<Canvas state={state} />
				<aside className="border-l border-white/10 bg-black/30 p-3">
					<p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-[#88d8de]">
						Docked Composer
					</p>
					{state.open ? (
						<div className="space-y-2">
							{hover ? (
								<p className="text-[11px] text-gray-400">
									Hovering {hover.name} · T adds it
								</p>
							) : null}
							<DraftFields state={state} />
							<ComposerActions state={state} />
						</div>
					) : (
						<p className="text-sm text-gray-500">
							Closed. T on a person or block, or hit +.
						</p>
					)}
					<div className="mt-3">
						<StateDump state={state} />
					</div>
				</aside>
			</div>
		</div>
	)
}

/** C — Composer covers the global search bar. */
export function VariantC({ state }: { state: PrototypeState }) {
	const items: ProtoRef[] =
		state.room === 'attendance' ? PEOPLE : BLOCKS
	return (
		<div className="flex min-h-screen flex-col bg-[#252828] text-white">
			<CaptureHotkeys state={state} />
			<header className="sticky top-0 z-30 border-b border-white/10 bg-[#303535]/90 backdrop-blur-md">
				<div className="flex min-h-16 items-center gap-3 px-4">
					<div className="relative min-w-0 flex-1">
						{state.open ? (
							<div className="space-y-1.5 py-2">
								<input
									autoFocus
									value={state.draft.action}
									onChange={(e) =>
										state.setDraft((d) => ({
											...d,
											action: e.target.value,
										}))
									}
									placeholder="What needs to happen?"
									className="w-full bg-transparent text-base text-white outline-none placeholder:text-gray-400"
								/>
								<div className="flex flex-wrap items-center gap-1">
									{state.draft.references.map((ref) => (
										<button
											key={ref.id}
											type="button"
											onClick={() => state.removeRef(ref.id)}
											className="rounded-full bg-[#88d8de]/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#88d8de]"
										>
											{ref.name} ×
										</button>
									))}
									<button
										type="button"
										onClick={state.save}
										className="rounded-md bg-[#ff585f] px-2 py-0.5 text-[10px] font-bold text-white"
									>
										Save
									</button>
									<button
										type="button"
										onClick={state.close}
										className="text-[10px] text-gray-400"
									>
										Esc
									</button>
								</div>
							</div>
						) : (
							<div className="relative flex h-10 items-center">
								<span className="pointer-events-none text-gray-400">
									<span className="underline">S</span>earch
								</span>
							</div>
						)}
					</div>
					<button
						type="button"
						onClick={() =>
							state.open ? state.newTask() : state.openComposer()
						}
						title="New Task"
						className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[#ff585f] text-sm font-bold text-white"
					>
						+
					</button>
					<div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-white text-xs font-bold text-[#303535]">
						KL
					</div>
				</div>
			</header>
			<div className="flex items-center gap-2 px-4 py-2 text-xs">
				{(
					[
						['attendance', 'Attendance'],
						['plan', 'Plan'],
					] as const
				).map(([key, label]) => (
					<button
						key={key}
						type="button"
						onClick={() => state.setRoom(key)}
						className={`rounded px-2 py-1 font-semibold ${
							state.room === key
								? 'bg-[#88d8de] text-[#303535]'
								: 'text-gray-300 hover:bg-white/10'
						}`}
					>
						{label}
					</button>
				))}
				<p className="text-gray-500">
					{state.open
						? 'Search is covered · hover + T adds a Reference'
						: 'Hover a person or block · T covers Search'}
				</p>
			</div>
			<div className="space-y-2 p-4">
				{items.map((item, i) => (
					<RefButton
						key={item.id}
						item={item}
						state={state}
						className="flex w-full items-center gap-3 rounded-md bg-white/5 px-3 py-2 text-left hover:bg-white/10"
					>
						<span className="flex size-6 items-center justify-center rounded-full bg-[#ff585f] text-xs font-bold">
							{i + 1}
						</span>
						<span className="text-sm">{item.name}</span>
					</RefButton>
				))}
			</div>
			<div className="mx-4 mb-20">
				<StateDump state={state} />
			</div>
		</div>
	)
}
